<script setup lang="ts">
import { copyTextToClipboard, formatDateTime } from '@metapic-apps/shared'

import CopyIcon from '@/assets/icons/copy.svg?component'
import type { AffiliateLink } from '@/lib/adapters/affiliate-links.adapter'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { BoxComponent, ButtonComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  link: AffiliateLink
}>()

const copyLink = () => copyTextToClipboard(props.link.mtpcUrl)
</script>

<template>
  <FlexComponent align="center" :gap="16" class="link-item">
    <FlexComponent align="center" :gap="13" class="link-item__brand">
      <BoxComponent v-if="link.image?.url" class="link-item__image-wrap">
        <img :src="link.image.url" :alt="link.title || link.advertiserName || ''" class="link-item__image" />
      </BoxComponent>
      <div v-else class="link-item__logo-wrap">
        <img v-if="link.advertiserLogoUrl" :src="link.advertiserLogoUrl" :alt="link.advertiserName || ''" class="link-item__logo" />
      </div>
      <FlexComponent direction="column" :gap="0" class="link-item__summary">
        <TypographyComponent variant="label-medium" color="var(--secondary-48)" class="link-item__store-name">
          {{ link.advertiserName || '—' }}
        </TypographyComponent>
        <TypographyComponent variant="title-large" class="link-item__title">
          {{ link.title || link.advertiserName || '—' }}
        </TypographyComponent>
        <FlexComponent align="center" :gap="4" class="link-item__url">
          <TypographyComponent variant="body-medium" class="link-item__url-text">{{ link.mtpcUrl }}</TypographyComponent>
          <ButtonComponent
            variant="transparent"
            class="link-item__copy"
            :is-icon="true"
            icon-color="#222222"
            aria-label="Copy link URL"
            @click.stop="copyLink"
          >
            <CopyIcon />
          </ButtonComponent>
        </FlexComponent>
      </FlexComponent>
    </FlexComponent>

    <FlexComponent :gap="8" class="link-item__data">
      <FlexComponent direction="column" align="center" :gap="4" class="link-item__field">
        <TypographyComponent variant="label-medium" color="var(--secondary-48)">ID</TypographyComponent>
        <TypographyComponent variant="title-large">{{ link.id }}</TypographyComponent>
      </FlexComponent>
      <FlexComponent direction="column" align="center" :gap="4" class="link-item__field">
        <TypographyComponent variant="label-medium" color="var(--secondary-48)">Created at</TypographyComponent>
        <TypographyComponent variant="title-large">{{ formatDateTime(link.createdAt) }}</TypographyComponent>
      </FlexComponent>
      <FlexComponent direction="column" align="center" :gap="4" class="link-item__field">
        <TypographyComponent variant="label-medium" color="var(--secondary-48)">Clicks</TypographyComponent>
        <TypographyComponent variant="title-large">{{ link.clicks }}</TypographyComponent>
      </FlexComponent>
      <FlexComponent direction="column" align="center" :gap="4" class="link-item__field link-item__earnings">
        <TypographyComponent variant="label-medium" color="var(--secondary-48)">Earnings</TypographyComponent>
        <TypographyComponent variant="title-large">
          {{ formatMoneyForAdmin(link.approvedEarnings, { allowZero: true }) }}
        </TypographyComponent>
        <TypographyComponent variant="label-medium" color="var(--secondary-48)">
          Pending {{ formatMoneyForAdmin(link.potentialEarnings, { allowZero: true }) }}
        </TypographyComponent>
      </FlexComponent>
    </FlexComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.link-item {
  padding: 16px;
  background: var(--inverted);
  border: 1px solid rgba(34, 34, 34, 0.08);
  border-radius: 16px;
  cursor: pointer;

  &:hover {
    border-color: rgba(34, 34, 34, 0.2);
  }

  &__brand {
    flex-shrink: 0;
    width: 520px;
    min-width: 0;
  }

  &__logo-wrap {
    flex-shrink: 0;
    width: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__logo {
    max-width: 60px;
    max-height: 24px;
    object-fit: contain;
  }

  &__image-wrap {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    overflow: hidden;
    border-radius: 12px;
    background: rgba(34, 34, 34, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__summary {
    min-width: 0;
  }

  &__title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__store-name {
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__url {
    min-width: 0;
    margin-top: -8px;
  }

  &__url-text {
    min-width: 0;
    overflow: hidden;
    color: rgba(34, 34, 34, 0.48);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__copy {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
  }

  :deep(.link-item__copy svg path) {
    fill: #222222;
  }

  &__data {
    flex: 1;
  }

  &__field {
    flex: 1;
    text-align: center;
  }
}
</style>
